/**
 * 報價單匯出「備註 Remark」欄的共用組字邏輯（需求 R96）。
 *
 * 為什麼要抽成共用檔：報價單 PDF（quotePdfExport.js）與 Excel（quoteExcelExport.js）是兩條
 * 完全獨立的程式路徑，歷史上只改一邊就會出現「PDF 兩行、Excel 一行」。兩邊都呼叫這裡的
 * buildRemarkLines()，行序與空行規則就只有一份定義，不會漂移（AC4）。
 *
 * PM 2026-07-31 逐字拍板：
 *   Q1 上下順序：葷素備註在上、業務備註在下。
 *   Q2 是否加標籤前綴：不加，只放內容，避免窄欄位換行爆版。
 *   Q3 其中一項為空時：**留空行**並印有值的行。
 *   Q4 兩項都為空時：留空白。
 *   Q5 舊報價單殘留的 notes（歷史資料）：不印，UI 已無此欄位。
 *
 * 註：Q3 的「留空行」與任務描述原本的預設（不留空行）相反，依 PM 留言為準；
 * 對應的 AC8 文字已於本次一併更正。
 */

/**
 * 空行用的佔位字元（不斷行空白 U+00A0）。
 *
 * 為何不用真正的空字串：
 * - HTML 端：`值<br>` 這種尾端 <br> 在部分瀏覽器不會產生行框，空行會消失；
 * - Excel 端：儲存格值尾端的 `\n` 是否撐出第二行同樣不保證。
 * 用 U+00A0 讓「空的那一行」在兩邊都確實佔一行，PDF 與 Excel 才會長得一樣（AC4）。
 * 此字元在 HTML 不需跳脫（原樣輸出即為不斷行空白），在 Excel 為純文字。
 */
export const REMARK_BLANK_LINE = '\u00A0';

/**
 * 取出一個報價單品項要印在備註欄的行（已套用 Q1～Q5 規則）。
 *
 * @param {object} item 報價單品項（Quote.js quoteItemSchema）
 * @returns {string[]} 長度 0（兩項皆空）或 2（第一行葷素、第二行業務；空的那行為 REMARK_BLANK_LINE）
 */
export function buildRemarkLines(item = {}) {
  // Q5：只讀 vegetarianNote / salesNotes，一律不讀已廢棄的 item.notes（新增報價單時固定寫入空字串）。
  const vegetarian = String(item?.vegetarianNote ?? '').trim();
  const sales = String(item?.salesNotes ?? '').trim();

  // Q4：兩項都空 → 完全留白，不輸出任何行（避免多出空行把列高／垂直置中弄歪）。
  if (!vegetarian && !sales) return [];

  // Q1：葷素在上、業務在下。Q2：不加前綴。Q3：其中一項為空仍保留該行位置。
  return [vegetarian || REMARK_BLANK_LINE, sales || REMARK_BLANK_LINE];
}

/**
 * Excel 用：同一儲存格內以換行字元串接（該欄已設 wrapText: true）。
 * @param {object} item 報價單品項
 * @returns {string}
 */
export function buildRemarkText(item) {
  return buildRemarkLines(item).join('\n');
}
